import type { IntegrationDto } from 'dtos/integration.dto';
import { SimpleEvent } from '@passionware/simple-event';
import { Middleware } from 'redux';
import { Callout } from '../../__types__/v1/models/Callout';
import { ResourceType } from '../../__types__/v1/models/ResourceType';
import { Group } from '__types__/v1/models/Group';
import type { Resource, Shortlist } from '__types__';

export type V1Action =
  | { type: 'GET_GROUPS' }
  | {
      type: 'GET_GROUPS_SUCCESS';
      result: { results: Group[]; paging: unknown };
    }
  | { type: 'GET_GROUPS_FAILURE'; error: unknown }
  | { type: 'CREATE_GROUP_SUCCESS' | 'UPDATE_GROUP_SUCCESS'; result: Group }
  | { type: 'REORDER_GROUPS_SUCCESS'; result: Group[] }
  | {
      type:
        | 'CREATE_PRODUCTION_SUCCESS'
        | 'UPDATE_PRODUCTION_SUCCESS'
        | 'DELETE_PRODUCTION_SUCCESS'
        | 'UNARCHIVE_PRODUCTION_SUCCESS';
    }
  | {
      type: 'CREATE_CONTACT_SUCCESS' | 'UPDATE_CONTACT_SUCCESS';
      /** The v1 shape: no `enterprise_workspaces`. */
      result: Omit<Resource, 'enterprise_workspaces'>;
      /** The request body; the callouts reducer reads `body.applicants` on create. */
      body?: unknown;
    }
  | { type: 'ARCHIVE_CONTACT_SUCCESS' | 'UNARCHIVE_CONTACT_SUCCESS' }
  | {
      type: 'GET_CONTACT_ASSOCIATIONS_SUCCESS';
      result: any;
    }
  | {
      type: 'UPDATE_RESOURCE_SLOT';
    }
  | {
      type: 'CREATE_BOOKING_SUCCESS';
      result: any;
    }
  | {
      type: 'UPDATE_BOOKING_SUCCESS';
      result: any;
    }
  | {
      type: 'DELETE_BOOKING_SUCCESS';
    }
  | {
      type: 'GET_BOOKINGS_SUCCESS';
      result: any;
    }
  | {
      type: 'GET_BOOKING_SUCCESS';
      result: any;
    }
  | {
      type: 'GET_BATCH_BOOKINGS_SUCCESS';
      result: any;
    }
  | {
      type: 'DELETE_RESOURCE_SLOT_ASSIGNMENT_SUCCESS';
    }
  | {
      type:
        | 'UPDATE_SHORTLIST_SUCCESS'
        | 'GET_PUBLIC_SHORTLIST_SUCCESS'
        | 'DELETE_SHORTLIST_SUCCESS'
        | 'UPDATE_SHORTLIST_BLOCK_SUCCESS'
        | 'COMMENT_SHORTLIST_BLOCK_SUCCESS'
        | 'CREATE_SHORTLIST_BLOCK_SUCCESS'
        | 'SHORTLIST_UPDATED';
    }
  | {
      type: 'CREATE_SHORTLIST_SUCCESS';
      /** The shortlists reducer keys `data` by `result.id` and keeps it as `new`. */
      result: Shortlist;
    }
  | {
      type: 'LOAD_AUTH_SUCCESS';
      result: {
        resource_types?: ResourceType[];
        settings: {
          [key: string]: unknown;
        };
        [key: string]: unknown;
      };
    }
  | {
      /** Legacy `PUT /settings/:field_key` — also used for non-catalog keys. */
      type: 'UPDATE_SETTINGS_SUCCESS';
      field_key: string;
    }
  | {
      type: 'accounts/getTeamAccounts/fulfilled';
    }
  | {
      type: 'GET_CALLOUTS_SUCCESS';
      result: {
        results: unknown[];
        paging: unknown;
      };
    }
  | {
      type:
        | 'GET_CALLOUT_SUCCESS'
        | 'CREATE_CALLOUT_SUCCESS'
        | 'UPDATE_CALLOUT_SUCCESS';
      result: Callout;
    }
  | {
      type: 'GET_CALLOUT_ASSOCIATIONS_SUCCESS';
      result: unknown;
      callout_id: number;
    }
  | {
      type: 'GET_PUBLIC_CALLOUT_SUCCESS';
      result: {
        callout: Callout;
        settings: unknown;
      };
    }
  | {
      type:
        | 'DELETE_CALLOUT_SUCCESS'
        | 'DELETE_APPLICANT_SUCCESS'
        | 'BATCH_ARCHIVE_CALLOUTS_SUCCESS'
        | 'BATCH_UNARCHIVE_CALLOUTS_SUCCESS';
    }
  | {
      type:
        | 'CREATE_INVOICES_SUCCESS'
        | 'UPDATE_INVOICE_SUCCESS'
        | 'DELETE_INVOICE_SUCCESS'
        | 'BOOKING_ARCHIVED'
        | 'BOOKING_CANCELLED'
        | 'BOOKING_CREATED'
        | 'BOOKING_UPDATED'
        | 'PRODUCTION_ARCHIVED'
        | 'PRODUCTION_CREATED'
        | 'PRODUCTION_UPDATED';
    }
  | {
      type: 'CREATE_EXPENSE_SUCCESS' | 'UPDATE_EXPENSE_SUCCESS';
      result: {
        id: number;
        booking_id?: number;
        production_budget_expense_id?: number;
      };
    }
  | {
      type: 'DELETE_EXPENSE_SUCCESS';
      id: number;
    }
  | {
      type:
        | 'CREATE_EXPENSE_RECEIPT_SUCCESS'
        | 'UPDATE_EXPENSE_RECEIPT_SUCCESS'
        | 'DELETE_EXPENSE_RECEIPT_SUCCESS';
      expense_item_id: number;
    }
  | {
      type: 'teams/getAccountTeams/fulfilled';
      payload: Array<{
        dbSchema: string;
        featureFlags?: unknown;
        __featureFlagsWire?: Array<{
          workspaceId: number | null;
          name: string;
        }>;
      }>;
    }
  | {
      type: 'integrations/getIntegrations/fulfilled';
      payload: IntegrationDto[];
    }
  | {
      type: 'integrations/configureIntegration/fulfilled';
      payload: IntegrationDto;
    }
  | {
      type: never;
    };

/**
 * Middleware that bridges V1 actions to a SimpleEvent to be consumed by other parts of the application.
 * @param event
 */
export function createActionAsEventMiddleware(
  event: SimpleEvent<V1Action>
): Middleware {
  return () => next => (action: V1Action) => {
    event.emit(action);
    return next(action);
  };
}
